import { ref, computed, watch, nextTick } from 'vue'
import { toValue } from 'vue'

import { useConfigStore } from '../stores/config'
import { usePageStore } from '../stores/page'
import { useKeepTabbingFocus } from './use-keep-tabbing-focus'
import { getSliderButtonBaseHeight } from '../utils/slider-button-position'

import type { Ref, MaybeRefOrGetter } from 'vue'
import type Flickity from 'flickity'

type ProductGroupLike = { productGroupType?: string } | null | undefined

export const flickitySliderProps = {
  sliderNo: { type: Number, default: 0 },
  initialIndex: { type: Number, default: 0 }
}

export function useFlickitySlider (
  flickityDivRef: Ref<HTMLElement | null>,
  options: {
    productGroup?: MaybeRefOrGetter<ProductGroupLike>,
    // set to false for sliders (e.g. the logo slider) where arrow keys shouldn't change slides
    enableArrowKeySlideChange?: boolean
  } = {}
) {
  const configStore = useConfigStore()
  const pageStore = usePageStore()

  const flickity = ref<Flickity | null>(null)

  // Owned here (rather than left for each caller to wire up separately) since every
  // consumer of keepTabbingFocusInSlider needs the exact same flickity ref anyway -
  // relying on getCurrentInstance().proxy to reach into the caller's own composable call
  // silently found nothing for <script setup> components that didn't retain the return value.
  const { keepTabbingFocus, removeTabbingEventListeners } = useKeepTabbingFocus(
    options.enableArrowKeySlideChange === false ? undefined : flickity
  )

  const sliderControlDescription = 'Karusell-Steuerung: Mit Enter Fokus auf Inhalte, mit Tab oder Escape verlässt du das Karusell. Mit Pfeiltasten Links/Rechts navigierst du jederzeit zwischen den Elementen. Mit Home und End gelangst du zum ersten bzw. letzten Element.'

  const sliderSettings = computed(() => configStore.rsConfig.slider)

  const sliderElements = computed(() => {
    const div = flickityDivRef.value
    return {
      cells: div?.querySelectorAll('.bx-slider__cell'),
      selectedCells: div?.querySelectorAll('.bx-slider__cell.is-selected'),
      imagesOfCellsWithoutInteractiveItems: div?.querySelectorAll('.bx-slider__cell:not(:has(a, button)) img'),
      aTags: Array.from(div?.querySelectorAll('.bx-slider__cell a') ?? []),
      buttons: Array.from(div?.querySelectorAll('.bx-slider__cell button') ?? []),
      iframes: div?.querySelectorAll('.bx-slider__cell iframe'),
      prevNextButtons: div?.getElementsByClassName('flickity-prev-next-button'),
      pageDots: div?.querySelectorAll('.flickity-page-dot'),
      placeholderButtons: div?.querySelectorAll('.bx-slider__cell .bx-placeholder__button')
    }
  })

  const isProductGroupSlider = computed(() =>
    toValue(options.productGroup)?.productGroupType === 'slider'
  )

  watch(() => pageStore.viewportMetaModified, (val) => {
    if (val && flickity.value) {
      triggerResize()
    }
  })

  async function initFlickitySlider (customOptions: Record<string, unknown> = {}) {
    await import('flickity/css/flickity.css')
    const Flickity = (await import('flickity')).default

    if (flickityDivRef.value && flickityDivRef.value.clientHeight > 0 && flickityDivRef.value.clientHeight < window.innerHeight) {
      flickityDivRef.value.setAttribute('style', `min-height:${flickityDivRef.value.clientHeight}px`)
    }

    const sliderOptions = {
      // options
      initialIndex: 0, // Zero-based index of the initial selected cell - Might be useful if reactive with URL
      contain: true, // Contains cells to carousel element to prevent excess scroll at beginning or end. Has no effect if wrapAround: true.
      cellAlign: 'left', // Align cells within the carousel element
      lazyLoad: 1, // number to load images in adjacent cells in advance
      pageDots: false, // display dots that can be used for navigation below image
      wrapAround: false, // infinitely scroll through your gallery, no start and no end point
      freeScroll: true, // scroll freely (so you can stop right inbetween 2 images if you really wanted to)
      prevNextButtons: true, // display nav buttons
      arrowShape: getArrowShape(), // generate the icons for buttons
      autoPlay: false, // Number in milliseconds to jump to next slide
      fullScreen: false, // enables/disables fullscreen view
      adaptiveHeight: false, // Changes height of carousel to fit height of selected slide.
      dragThreshold: 10, // The number of pixels a mouse or touch has to move before dragging begins. Increase dragThreshold to allow for more wiggle room for vertical page scrolling on touch devices. Default dragThreshold: 3.
      accessibility: true, // Enable keyboard navigation
      percentPosition: true, // sets positioning in percent values, rather than pixels
      groupCells: true, // group cells together in slides
      resize: true, // listens to window resize events to adjust size & positions
      setGallerySize: true, // sets the height of gallery
      imagesLoaded: false, // the imagesLoaded option re-positions cells once their images have loaded
      cellSelector: '.bx-slider__cell', // useful if you have other elements in your carousel elements that are not cells
      on: {
        ready: () => {
          triggerResize()
          initTabindices()
          setAriaAttributes()
        }
      }
    }

    if (!flickityDivRef.value) return
    flickity.value = new Flickity(flickityDivRef.value, { ...sliderOptions, ...customOptions })

    document.addEventListener('readystatechange', () => {
      if (document.readyState === 'complete') {
        nextTick(() => {
          triggerResize()
        })
      }
    })
  }

  function getArrowShape () {
    return sliderSettings.value?.nextPrevArrowPath
  }

  function triggerResize () {
    flickityDivRef.value?.removeAttribute('style')

    if (flickity.value !== undefined && flickity.value !== null && typeof flickity.value.resize === 'function') {
      flickity.value?.resize()
      checkButtonPosition()
    }
  }

  function checkButtonPosition () {
    if ((sliderElements.value.prevNextButtons?.length ?? 0) > 0) {
      const activeCell = sliderElements.value.selectedCells?.[0] as HTMLElement | undefined
      const positionLogic = activeCell?.dataset.buttonPositionLogic

      if (positionLogic) {
        let baseHeight: number | false = false
        let calcTop = '50%'

        if (activeCell) baseHeight = getSliderButtonBaseHeight(activeCell, positionLogic)

        if (baseHeight) {
          const computedStyle = window.getComputedStyle(flickityDivRef.value!)
          const paddingTop = parseFloat(computedStyle.paddingTop) || 0
          calcTop = `${(baseHeight / 2) + paddingTop}px`
        }

        Array.from(sliderElements.value.prevNextButtons ?? []).forEach(btn => {
          if (btn instanceof HTMLElement) btn.style.top = calcTop
        })
      }
    }
    flickityDivRef.value?.classList.remove('bx-js-hide-buttons')
  }

  function setFocusableElementsSelector (): string[] {
    return isProductGroupSlider.value
      ? ['.bx-product']
      : ['.bx-slider__cell a', '.bx-slider__cell .bx-placeholder__button', '.bx-slider__cell:not(:has(a, button)) img', '.bx-slider__cell .bx-content--text p a']
  }

  function getFirstSelectedCellIndex (): number | undefined {
    const cellIndex = flickityDivRef.value?.querySelector('.bx-slider__cell.is-selected')?.getAttribute('data-cell-index')
    return cellIndex !== undefined && cellIndex !== null ? Number(cellIndex) : undefined
  }

  function setAttributeForElements (
    elements: NodeListOf<Element> | HTMLCollectionOf<Element> | Element[] | null | undefined,
    attribute: string,
    value: string | string[] | boolean
  ): void {
    if (!elements?.length) return
    Array.from(elements as ArrayLike<Element>).forEach((e, index) => {
      e.setAttribute(attribute, Array.isArray(value) ? value[index] : String(value))
    })
  }

  function initTabindices () {
    setAttributeForElements(sliderElements.value.pageDots, 'tabindex', '-1')
    setAttributeForElements(sliderElements.value.prevNextButtons, 'tabindex', '-1')
    setAttributeForElements(sliderElements.value.aTags, 'tabindex', '-1')
    setAttributeForElements(sliderElements.value.buttons, 'tabindex', '-1')
    setAttributeForElements(sliderElements.value.iframes, 'tabindex', '-1')
    setAttributeForElements(sliderElements.value.imagesOfCellsWithoutInteractiveItems, 'tabindex', '-1')
  }

  function updateCellInertState () {
    const cells = sliderElements.value.cells
    if (!cells) return
    cells.forEach(cell => {
      if (cell.classList.contains('is-selected')) {
        cell.removeAttribute('inert')
      } else {
        cell.setAttribute('inert', '')
      }
    })
  }

  function setAriaAttributes () {
    const div = flickityDivRef.value
    const teasers = div?.querySelectorAll('.bx-slider__cell .bx-teaser__headline') ?? []
    const hotGossip = div?.querySelectorAll('.bx-slider__cell .bx-hot-gossip__headline') ?? []
    const starsigns = div?.querySelectorAll('.bx-slider--starsign-slider a') ?? []
    const logos = div?.querySelectorAll('.bx-slider--logo-slider a') ?? []
    const logoTexts = Array.from(logos).map(el => (el as HTMLAnchorElement).title)
    const imgCaptions = div?.querySelectorAll('.bx-slider__cell .bx-content__media-caption a') ?? []
    const continuousText = div?.querySelectorAll('.bx-slider__cell .bx-content__continuous-text a') ?? []
    const textfulEls = [...teasers, ...hotGossip, ...starsigns, ...imgCaptions, ...continuousText]
    const slidesAriaLabels = [
      ...textfulEls.map(el => el.textContent),
      ...logoTexts
    ].map((text, index) => `${text}, Link ${index + 1} von ${textfulEls.length}`)

    setAttributeForElements(sliderElements.value.aTags, 'aria-label', slidesAriaLabels)
    setAttributeForElements(sliderElements.value.aTags, 'aria-roledescription', 'Element')
    div?.setAttribute('role', 'group')
    div?.setAttribute('aria-roledescription', 'Karusell')
  }

  function keepTabbingFocusInSlider () {
    if (isProductGroupSlider.value || !flickityDivRef.value) return
    setAttributeForElements(sliderElements.value.cells, 'aria-hidden', false)
    setAttributeForElements(sliderElements.value.aTags, 'tabindex', '0')
    setAttributeForElements(sliderElements.value.buttons, 'tabindex', '0')

    const tabEscCallback = () => {
      removeTabbingEventListeners()
      setAttributeForElements(sliderElements.value.aTags, 'tabindex', '-1')
      setAttributeForElements(sliderElements.value.buttons, 'tabindex', '-1')
      setAttributeForElements(sliderElements.value.imagesOfCellsWithoutInteractiveItems, 'tabindex', '-1')
      flickityDivRef.value?.focus()
    }

    nextTick(() => {
      if (!flickityDivRef.value) return
      removeTabbingEventListeners()
      keepTabbingFocus(
        flickityDivRef.value,
        setFocusableElementsSelector(),
        getFirstSelectedCellIndex(),
        tabEscCallback,
        tabEscCallback
      )
    })
  }

  return {
    flickity,
    sliderControlDescription,
    sliderSettings,
    sliderElements,
    isProductGroupSlider,
    initFlickitySlider,
    getArrowShape,
    triggerResize,
    checkButtonPosition,
    setFocusableElementsSelector,
    getFirstSelectedCellIndex,
    setAttributeForElements,
    initTabindices,
    updateCellInertState,
    setAriaAttributes,
    keepTabbingFocusInSlider
  }
}
